import React, { useState, useEffect } from "react";
import { Hexagon, LogOut, Settings, X, User, Mail, Phone, Menu, Pencil, Check, Loader2, Bell } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useNotifications } from "../../contexts/NotificationContext";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { getUserSettings, updateUserSettings } from "../../lib/api";

const ROLE_LABEL = {
  FleetAdmin: "Fleet Admin",
  ReleaseManager: "Release Manager",
  SupportEngineer: "Support Engineer",
};

function initials(name = "") {
  return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
}

/**
 * Notification bell with unread count and a live-connection dot.
 *
 * The dot is deliberately visible: because delivery depends on an open socket,
 * a user who can see "not connected" understands why nothing is arriving. A
 * silent bell would look identical to a working one with no news.
 */
function NotificationBell() {
  const { supported, connected, notifications, unreadCount, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);

  if (!supported) return null;

  // Kept out of the JSX so the aria-label is a single, flat template literal.
  const unreadSuffix = unreadCount ? ` (${unreadCount} unread)` : "";

  return (
    <div className="relative">
      <button
        onClick={() => { setOpen((o) => !o); if (!open) markAllRead(); }}
        className="relative flex items-center gap-1.5 text-blue-200 hover:text-white text-xs border border-[#3333d1] hover:border-blue-300 rounded-md px-2 sm:px-3 py-1.5 transition-colors"
        aria-label={`Notifications${unreadSuffix}`}
      >
        <Bell className="h-3.5 w-3.5" />
        <span
          className={`absolute -bottom-0.5 -left-0.5 h-1.5 w-1.5 rounded-full ${
            connected ? "bg-emerald-400" : "bg-slate-400"
          }`}
          title={connected ? "Live" : "Reconnecting…"}
        />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Click-catcher only; the bell button is the keyboard affordance, so
              this stays out of the accessibility tree. */}
          <div className="fixed inset-0 z-40" aria-hidden="true" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-xl shadow-2xl border border-slate-100 z-50">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <p className="text-xs font-semibold text-slate-700">Notifications</p>
              <span className={`text-[10px] font-medium ${connected ? "text-emerald-600" : "text-slate-400"}`}>
                {connected ? "● Live" : "○ Reconnecting…"}
              </span>
            </div>
            {notifications.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-8">Nothing yet.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((n) => (
                  <div key={n.id} className="px-4 py-3">
                    <p className="text-xs font-semibold text-slate-800">{n.header}</p>
                    <p className="text-xs text-slate-600 mt-0.5 leading-snug">{n.content}</p>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SettingsModal({ onClose, settings, error, onSettingsUpdated }) {
  const { role } = useAuth();

  const displayName = settings?.full_name || settings?.name || "";
  const avatarInitials = displayName
    ? displayName.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)
    : "??";

  // Edit state
  const [editingName, setEditingName] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const _fullParts = (settings?.full_name || settings?.name || "").trim().split(/\s+/);
  const [nameFields, setNameFields] = useState({
    first_name:  settings?.first_name  || _fullParts[0] || "",
    middle_name: settings?.middle_name || (_fullParts.length > 2 ? _fullParts.slice(1, -1).join(" ") : "") || "",
    last_name:   settings?.last_name   || (_fullParts.length > 1 ? _fullParts[_fullParts.length - 1] : "") || "",
  });
  const [phoneField, setPhoneField] = useState(settings?.phone_number || settings?.phone || "");

  // Sync when settings load
  useEffect(() => {
    if (settings) {
      // API may return full_name as a single string; split it as fallback.
      const fullParts = (settings.full_name || settings.name || "").trim().split(/\s+/);
      setNameFields({
        first_name:  settings.first_name  || fullParts[0] || "",
        middle_name: settings.middle_name || (fullParts.length > 2 ? fullParts.slice(1, -1).join(" ") : "") || "",
        last_name:   settings.last_name   || (fullParts.length > 1 ? fullParts[fullParts.length - 1] : "") || "",
      });
      setPhoneField(settings.phone_number || settings.phone || "");
    }
  }, [settings]);

  async function handleSave(section) {
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        first_name: nameFields.first_name,
        middle_name: nameFields.middle_name,
        last_name: nameFields.last_name,
        phone_number: phoneField,
      };
      const updated = await updateUserSettings(payload);
      onSettingsUpdated({ ...settings, ...payload, ...updated });
      if (section === "name") setEditingName(false);
      if (section === "phone") setEditingPhone(false);
    } catch (err) {
      setSaveError(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Decorative backdrop; keyboard users close via the header's X button. */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

        {/* Modal header bar */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-800 tracking-wide">Account Settings</h2>
          <button onClick={onClose} className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Profile summary */}
        <div className="flex items-center gap-5 px-6 py-6 bg-slate-50 border-b border-slate-100">
          <div className="h-16 w-16 rounded-full bg-[#08148C] flex items-center justify-center text-white text-lg font-bold shrink-0">
            {avatarInitials}
          </div>
          <div>
            <p className="text-base font-semibold text-slate-900 leading-tight">{displayName || "—"}</p>
            {role && (
              <span className="inline-flex items-center gap-1.5 mt-1 text-[11px] font-medium text-slate-500">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                {ROLE_LABEL[role] || role}
              </span>
            )}
          </div>
        </div>

        {/* Fields */}
        {error && (
          <p className="text-sm text-red-500 text-center py-8">{error}</p>
        )}
        {!error && settings && (
          <div className="px-6 py-2 divide-y divide-slate-100">

            {/* Full Name row */}
            <div className="flex items-start gap-3 py-3.5">
              <User className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 leading-none">Full Name</p>
                  {!editingName && (
                    <button onClick={() => setEditingName(true)} className="flex items-center gap-1 text-[11px] text-[#08148C] hover:underline">
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                  )}
                </div>
                {editingName ? (
                  <div className="space-y-2 mt-1">
                    <input
                      className="w-full text-sm border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#08148C]/30"
                      placeholder="First Name"
                      value={nameFields.first_name}
                      onChange={(e) => setNameFields((p) => ({ ...p, first_name: e.target.value }))}
                    />
                    <input
                      className="w-full text-sm border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#08148C]/30"
                      placeholder="Middle Name (optional)"
                      value={nameFields.middle_name}
                      onChange={(e) => setNameFields((p) => ({ ...p, middle_name: e.target.value }))}
                    />
                    <input
                      className="w-full text-sm border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#08148C]/30"
                      placeholder="Last Name"
                      value={nameFields.last_name}
                      onChange={(e) => setNameFields((p) => ({ ...p, last_name: e.target.value }))}
                    />
                    {saveError && <p className="text-xs text-red-500">{saveError}</p>}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSave("name")}
                        disabled={saving}
                        className="flex items-center gap-1 text-xs bg-[#08148C] text-white px-3 py-1.5 rounded-md hover:bg-[#0d1fa8] disabled:opacity-60"
                      >
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
                      </button>
                      <button
                        onClick={() => { setEditingName(false); setSaveError(null); }}
                        className="text-xs text-slate-500 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-800">{displayName || "—"}</p>
                )}
              </div>
            </div>

            {/* Email row (read-only) */}
            <div className="flex items-center gap-3 py-3.5">
              <Mail className="h-4 w-4 text-slate-400 shrink-0" />
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 leading-none mb-0.5">Email</p>
                <p className="text-sm text-slate-800">{settings.email || "—"}</p>
              </div>
            </div>

            {/* Phone row */}
            <div className="flex items-start gap-3 py-3.5">
              <Phone className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 leading-none">Phone Number</p>
                  {!editingPhone && (
                    <button onClick={() => setEditingPhone(true)} className="flex items-center gap-1 text-[11px] text-[#08148C] hover:underline">
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                  )}
                </div>
                {editingPhone ? (
                  <div className="space-y-2 mt-1">
                    <input
                      className="w-full text-sm border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#08148C]/30"
                      placeholder="+917034294007"
                      value={phoneField}
                      onChange={(e) => setPhoneField(e.target.value)}
                    />
                    {saveError && <p className="text-xs text-red-500">{saveError}</p>}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSave("phone")}
                        disabled={saving}
                        className="flex items-center gap-1 text-xs bg-[#08148C] text-white px-3 py-1.5 rounded-md hover:bg-[#0d1fa8] disabled:opacity-60"
                      >
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
                      </button>
                      <button
                        onClick={() => { setEditingPhone(false); setSaveError(null); }}
                        className="text-xs text-slate-500 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-800">{settings.phone_number || settings.phone || "—"}</p>
                )}
              </div>
            </div>

          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50" />
      </div>
    </div>
  );
}

export function Header({ onMenuClick }) {
  const { user, role, logout } = useAuth();
  const [showSettings, setShowSettings] = useState(false);
  const [settingsData, setSettingsData] = useState(null);
  const [settingsError, setSettingsError] = useState(null);

  useEffect(() => {
    getUserSettings()
      .then((data) => setSettingsData(data))
      .catch((err) => setSettingsError(err.message));
  }, []);

  return (
    <>
      <header className="h-16 bg-[#08148C] flex items-center justify-between px-4 sm:px-6 shrink-0">
        {/* Left: hamburger (mobile) + brand */}
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={onMenuClick}
            className="p-2 rounded-md text-blue-200 hover:text-white hover:bg-[#0d1fa8] transition-colors"
            aria-label="Toggle sidebar"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#0d1fa8] text-blue-200">
              <Hexagon className="h-5 w-5" />
            </div>
            <div className="hidden sm:block">
              <p className="text-white font-bold text-sm leading-none">
                TETRA MICRO INFUSION SYSTEM
              </p>
              <p className="text-blue-200 text-[11px] mt-0.5 leading-none">Fleet Management Portal</p>
            </div>
          </div>
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* App version */}
          <span className="hidden sm:inline-flex items-center bg-[#0d1fa8]/60 border border-[#1428b0] rounded-full px-3 py-1 text-lg font-medium text-white">
            v1.0.39
          </span>

          {/* Role badge · hidden on small screens */}
          <div className="hidden sm:flex items-center gap-1.5 bg-[#0d1fa8]/60 border border-[#1428b0] rounded-full px-3 py-1">
            <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
            <span className="text-white text-xs font-medium">{ROLE_LABEL[role] || role}</span>
          </div>

          {/* Avatar */}
          <Avatar className="h-8 w-8 cursor-pointer">
            <AvatarFallback className="bg-[#3333d1] text-white text-xs font-semibold">
              {initials(user?.name)}
            </AvatarFallback>
          </Avatar>

          {/* Notifications */}
          <NotificationBell />

          {/* Settings */}
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center gap-1.5 text-blue-200 hover:text-white text-xs border border-[#3333d1] hover:border-blue-300 rounded-md px-2 sm:px-3 py-1.5 transition-colors"
          >
            <Settings className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Settings</span>
          </button>

          {/* Sign out */}
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-blue-200 hover:text-white text-xs border border-[#3333d1] hover:border-blue-300 rounded-md px-2 sm:px-3 py-1.5 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </header>

      {showSettings && (
        <SettingsModal
          onClose={() => setShowSettings(false)}
          settings={settingsData}
          error={settingsError}
          onSettingsUpdated={(updated) => setSettingsData(updated)}
        />
      )}
    </>
  );
}
